/* global chrome */
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { Button, Card, CardHeader, CardMedia, CardContent, CardActions, Avatar, List, ListItemText, ButtonBase, IconButton, Typography, Badge, CardActionArea, Box, Paper, Chip, Popover, MenuList, MenuItem, Tooltip } from '@mui/material';
import { red, grey } from '@mui/material/colors';
import ShareIcon from '@mui/icons-material/Share';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import CircularProgress from '@mui/material/CircularProgress';

import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpAltTwoToneIcon from '@mui/icons-material/ThumbUpAltTwoTone';
import ThumbDownAltTwoToneIcon from '@mui/icons-material/ThumbDownAltTwoTone';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

import ReplyIcon from '@mui/icons-material/Reply';

import noFavicon from './no_favicon.svg';


import { RateReview } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
//import produce from 'immer';
import { useImmer } from "use-immer";
import { usePersonStore } from 'stores';
import { stopEventPropagation } from 'tldraw';
import { CARD_IMAGE_HOVER_ENTER_DELAY_MS, getCardImageHoverScale } from 'constants/cardImageZoom';
import { getCardGridSpan, getCardWidthFromGridSpan } from 'constants/cardLayout';
import { getScreenshotTiers } from 'utils/screenshotTiers';
import { getPinThumbnail, getPinnedComment, isPageOnlyPin, getPinSnippet, getHighlightDimensions, getBoardYouTubeVideo } from 'canvas/utils/autoLayout';
import { getYouTubeVideoFromActivity, isGenericYouTubeLabel } from 'canvas/utils/youtubeUrl';
import { resolveDisplayTitle } from 'canvas/utils/pinTitle';
import { LEDGER_ACCENT_ON_LIGHT } from 'canvas/utils/ledgerAccent';
import {
	boardInvZoomPx,
	boardPinHeaderHeightPx,
	boardPinHeaderScreenPx,
	readBoardInvZoom,
} from 'canvas/utils/boardCameraCss';
import LazyImage from 'components/LazyImage';

const youtubeMomentChipStyle = {
	position: 'absolute',
	left: 8,
	bottom: 8,
	zIndex: 1,
	padding: '2px 6px',
	borderRadius: 6,
	background: 'rgba(0, 0, 0, 0.72)',
	color: '#fff',
	fontSize: 12,
	lineHeight: '16px',
	fontVariantNumeric: 'tabular-nums',
	pointerEvents: 'none',
};

function YouTubeMomentChip({ label }) {
	if (!label) return null;
	return <span style={youtubeMomentChipStyle}>{label}</span>;
}

/** Canvas pin card chrome scale relative to list/feed cards (padding, actions). */
const BOARD_PIN_HEADER_SCALE = 0.8;
/** Minimum width for board pin comment block (may exceed narrow pin screenshots). */
const BOARD_PIN_COMMENT_MIN_WIDTH_PX = 360;

function stripParsedTagsFromText(text) {
	const safeText = typeof text === 'string' ? text : '';
	return safeText
		.replace(/(^|\s)@([a-zA-Z0-9_.-]+)/g, ' ')
		.replace(/(^|\s)#([a-zA-Z0-9_]+)/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

const Content = (props) => {
	
	const { query } = props;
	const [pageNum, setPageNum] = useState(0);
	const [imgLoaded, setImgLoaded] = useState(false);
	const [isImageHovered, setIsImageHovered] = useState(false);
	const [isCommentLoadingCompleted, setIsCommentLoadingCompleted] = useState(false);
	const [isCommentFormClicked, setIsCommentFormClicked] = useState(false);
	const user = useSelector(state => state.user);
	const dispatch = useDispatch();
	const { root, extensionInstalled, appType, extensionId } = useSelector(state => state.api);
	const { loadedTime } = props;
	const [lastTransformOrigin, setLastTransformOrigin] = useState('center');
	const lastTransformOriginRef = useRef('center'); // <-- add this

	const [contextMenuPosition, setContextMenuPosition] = React.useState(null);
	const [isBoardPinHovered, setIsBoardPinHovered] = useState(false);
	const boardPinHoverTimeoutRef = useRef(null);
	const imageHoverEnterTimeoutRef = useRef(null);
	const personStore = usePersonStore();

	const handleClose = () => {
	  setContextMenuPosition(null);
	};

	const handleContextMenu = (event) => {
		if (props.type === 'OBJECT' || props.type === 'BOARD') return;
		event.preventDefault();
		setContextMenuPosition({
			top: event.clientY,
			left: event.clientX,
		});
	};

	const getPinnedCommentId = () => {
		const action = props.activity?.action;
		if (action?.commentId) return String(action.commentId);
		if (action?._id && action?.highlightData) return String(action._id);
		if (action?._id && !action?.replyId) return String(action._id);
		return null;
	};

	/** Board hydrate keeps only latest comments; ensure the pin thread is present for the extension. */
	const contentForAutoOpen = (commentId) => {
		const action = props.activity?.action;
		const comments = Array.isArray(content?.comments) ? content.comments : [];
		if (!commentId || !content) return content;
		if (comments.some(c => String(c._id) === String(commentId))) {
			return {
				...content,
				comments: comments.filter(c => String(c._id) === String(commentId)),
				commentCount: 1,
			};
		}
		if (
			action?._id &&
			String(action._id) === String(commentId) &&
			(action.highlightData || action.positionData)
		) {
			return {
				...content,
				comments: [action],
				commentCount: 1,
			};
		}
		return content;
	};

	const getPageHref = () => content.urls?.[0]?.href || null;

	const handleDeleteActivity = () => {
		//show alert message to check if the user really wants to delete the activity
		const okay = window.confirm('Are you sure you want to delete this pin?');
		if(okay){
			handleClose();
			personStore.deleteActivity(props.activity)
		}
	}
	
	/*
		const makeUpdater = (updateValue) => {
			return updater => {
				updateValue(
					produce(updater)
				);
		}}	
		const updateContent = makeUpdater(setContent); 
	*/

	const [content, updateContent] = useImmer(
		props.activity.content[0]
	);

	useEffect(() => {
		updateContent(() => props.activity.content[0]);
	}, [props.activity, updateContent]);

	const	handleCommentFormClick = () => {
		setIsCommentFormClicked(prev => !prev)
	};

	const handleContentLike = () => {
    if(!user)return dispatch({ type: 'OPEN_SIGN_$'})
		if(content.userReaction.length === 0){
			fetch(`${root}/api/reaction/content/${content._id}/1/0`, { method: 'put', credentials: 'include' })
				.then(res => console.log('reaction update success'))
				.catch(err => console.log(err))
			updateContent(draft => { 
				draft.likeCount++; 
				draft.userReaction = [{ type: 1 }]
			})
			return;
		} else {
		switch (content.userReaction[0].type){ 
			case -1:
				fetch(`${root}/api/reaction/content/${content._id}/1/-1`, { method: 'put', credentials: 'include' })
					.then(res => console.log('reaction update success'))
					.catch(err => console.log(err))
				updateContent(draft => { 
					draft.likeCount++; 
					draft.dislikeCount--;
					draft.userReaction[0].type = 1;
				});
				break;
			case 0:
				fetch(`${root}/api/reaction/content/${content._id}/1/0`, { method: 'put', credentials: 'include' })
					.then(res => console.log('reaction update success'))
					.catch(err => console.log(err))
				updateContent(draft => { 
					draft.likeCount++; 
					draft.userReaction[0].type = 1;
				})
				break;
			case 1:
				fetch(`${root}/api/reaction/content/${content._id}/1/1`, { method: 'put', credentials: 'include' })
					.then(res => console.log('reaction update success'))
					.catch(err => console.log(err))
				updateContent(draft => { 
					draft.likeCount--; 
					draft.userReaction[0].type = 0;
				})
				break;
		}
		}
	}

	const handleContentDislike = () => {
    if(!user)return dispatch({ type: 'OPEN_SIGN_$' });
		if(content.userReaction.length === 0){
			fetch(`${root}/api/reaction/content/${content._id}/-1/0`, { method: 'put', credentials: 'include' })
				.then(res => console.log('reaction update success'))
				.catch(err => console.log(err))
			updateContent(draft => { 
				draft.dislikeCount++; 
				draft.userReaction = [{ type: -1 }];
			})
			return;
		} else {
		switch (content.userReaction[0].type){ 
			case -1:
				fetch(`${root}/api/reaction/content/${content._id}/-1/-1`, { method: 'put', credentials: 'include' })
					.then(res => console.log('reaction update success'))
					.catch(err => console.log(err))
				updateContent(draft => { 
					draft.dislikeCount--;
					draft.userReaction[0].type = 0;
				})
				break;
			case 0:
				fetch(`${root}/api/reaction/content/${content._id}/-1/0`, { method: 'put', credentials: 'include' })
					.then(res => console.log('reaction update success'))
					.catch(err => console.log(err))
				updateContent(draft => { 
					draft.dislikeCount++; 
					draft.userReaction[0].type = -1;
				})
				break;
			case 1:
				fetch(`${root}/api/reaction/content/${content._id}/-1/1`, { method: 'put', credentials: 'include' })
					.then(res => console.log('reaction update success'))
					.catch(err => console.log(err))
				updateContent(draft => { 
					draft.likeCount--; 
					draft.dislikeCount++; 
					draft.userReaction[0].type = -1;
				})
				break;
			} 
		}}

		const handleClearClick = e => {
			dispatch({type: 'HIDE_ON_ACTIVITIES', content })
			e.stopPropagation();			
			e.preventDefault();
		}

  /*
	let comments = 
		content.comments.length > 0 
		?
			<div style={{display:'flext', flexDirection: 'column'}}>
				{content.comments.map((comment, index) => (
					<Comment contentId={content._id} comment={comment} updateContent={updateContent}/>
				))}
			</div>
		:
			null
		;

		action={
			<IconButton aria-label="settings">
				<MoreVertIcon />
			</IconButton>
    */

    const { metas } = content;
    let description;
    const pinThumbnail = getPinThumbnail(props.activity);
    if (metas) {
      description = metas['description']||metas['Description']||metas['og:description']||null;
    }

    const shortDomain = content.urls[0].hostname.substring(0, 4) === 'www.'? content.urls[0].hostname.substring(4) : content.urls[0].hostname;

  	function openCenteredWindow(url) {
		// Get the dimensions of the current window
		var currentWidth = window.screen.width;
		var currentHeight = window.screen.height;
		
		// Calculate 80% of the current window size
		var newWidth = currentWidth * 0.8;
		var newHeight = currentHeight * 0.8;
		
		// Calculate the top-left position to center the new window
		var left = (currentWidth - newWidth) / 2;
		var top = (currentHeight - newHeight) / 2;
		
		console.log('openCenter url is', currentWidth, newWidth, left, top)
	    // Create the features string
	    // var windowFeatures = `width=${newWidth},height=${newHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`;

		var windowFeatures = `width=${window.screen.width},height=${newHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`;

	    // Open the new window
	    var windowName = "newCenteredWindow"; // Name of the new window

	    var newWindow = window.open(url, windowName, windowFeatures);
		newWindow.onload = function() {
			newWindow.scrollTo((newWindow.document.body.scrollWidth - newWindow.innerWidth) / 2, 0);
		};
	} 

    const savedYouTube = getYouTubeVideoFromActivity(props.activity);
    const youtubeVideo = props.type === 'BOARD' ? getBoardYouTubeVideo(props.activity) : null;

    const handleAreaClick = e => {
		if (youtubeVideo && typeof props.onOpenYoutube === 'function') {
			e?.preventDefault?.();
			e?.stopPropagation?.();
			props.onOpenYoutube({
				...youtubeVideo,
				activityId: props.activity?._id || props.activity?.actionId || null,
			});
			return;
		}
		const pageHref = getPageHref();
		if (!pageHref) return;
		const commentId = getPinnedCommentId();
		const openContent = contentForAutoOpen(commentId);
    	if(appType == 'EXTENSION'){
	    	chrome.storage.sync.set({ isVisible: true, visiblePanel: 'auto' });
	     	chrome.runtime.sendMessage(extensionId, {
				type: 'OPEN_AUTO_CONTENT',
				contentId: content._id,
				commentId,
				pageHref,
				content: openContent,
			}, function() {});
    	} else {
    		if(!extensionInstalled){
    			const okay = window.confirm(
					"Please install the extension to track the precise target"
				)
				if(okay){
					window.open('https://chrome.google.com/webstore/detail/socialbrowse-chrome/malnhaodjdegcljjebmpopbjmckblndn')
					return;
				} else {
					openCenteredWindow(pageHref);
					return;
				}
		}
	      chrome.runtime.sendMessage(extensionId, {
				type: 'OPEN_AUTO_CONTENT',
				contentId: content._id,
				commentId,
				pageHref,
				content: openContent,
			}, function() {});
    	}
		}

		const inverseZoom = props.type === 'CANVAS' ? 1 / props.moveScale : (props.inverseZoom ?? 1);
		const isBoard = props.type === 'BOARD';
		const boardHeaderScale = isBoard ? BOARD_PIN_HEADER_SCALE : 1;
		const allowPinInteraction = !isBoard || props.allowPinInteraction !== false;
		const pinDragPointerEvents = isBoard && allowPinInteraction ? 'all' : undefined;
		const blockCanvasDrag = isBoard && !allowPinInteraction
			? (e) => {
				e.preventDefault();
				stopEventPropagation(e);
			}
			: isBoard
				? (e) => {
					e.preventDefault();
				}
				: undefined;
		const { elementWidth } = getHighlightDimensions(props.activity);
		// Grid/board: fill the card. Absolute elementWidth px fights CSS grid span
		// (min-content can force the card wider than its column span).
		const imageAreaWidth = (isBoard || props.isBookmarkLayout)
			? '100%'
			: (elementWidth ? `${elementWidth}px` : '100%');
		const cardLayoutWidth = isBoard
			? (props.cardLayoutWidth || 300)
			: getCardWidthFromGridSpan(getCardGridSpan(elementWidth));
		const viewZoom = isBoard
			? (props.viewZoom ?? 1)
			: (inverseZoom > 0 ? 1 / inverseZoom : 1);
		const screenshotTiers = getScreenshotTiers(props.activity?.action, getPinnedComment(props.activity));
		const hasAdaptiveScreenshot = Boolean(screenshotTiers.full);
		const pinnedCommentId = getPinnedCommentId();
		const pinComment = pinnedCommentId
			? content.comments?.find(comment => String(comment._id) === pinnedCommentId)
			: content.comments?.[0];
		const replies = pinComment?.replies;
		const lastReply = Array.isArray(replies) && replies.length > 0
			? replies[replies.length - 1]
			: null;
		const totalReplyCount = Math.max(
			pinComment?.replyCount ?? 0,
			Array.isArray(replies) ? replies.length : 0,
		);
		const moreRepliesCount = lastReply && totalReplyCount > 1 ? totalReplyCount - 1 : 0;
		const pinCommentWriter = pinComment?.writer?.[0];
		const lastReplyWriter = lastReply?.writer?.[0];
		const pinCommentPlainText = pinComment
			? stripParsedTagsFromText(pinComment.text || pinComment.body || '')
			: '';
		const pageOnlyPin = isBoard && isPageOnlyPin(props.activity);
		const pageHref = getPageHref();
		const rawBoardTitle = pageOnlyPin
			? (content.title || pinCommentPlainText || getPinSnippet(props.activity))
			: (pinCommentPlainText || content.title);
		const snippet = getPinSnippet(props.activity);
		const genericYoutubeTitle = Boolean(youtubeVideo) && isGenericYouTubeLabel(rawBoardTitle);
		const boardPinHeaderTitle = isBoard
			? resolveDisplayTitle(
				genericYoutubeTitle ? '' : rawBoardTitle,
				genericYoutubeTitle ? '' : pageHref,
				{
					snippet: youtubeVideo && isGenericYouTubeLabel(snippet) ? '' : snippet,
					fallback: youtubeVideo
						? 'YouTube video'
						: (content.urls?.[0]?.hostname || content.urls?.[0]?.host),
				},
			)
			: content.title;
		const ledgerHeaderText = isBoard ? String(props.ledgerHeaderText || '').trim() : '';
		const headerTitleText = ledgerHeaderText || boardPinHeaderTitle;
		// Board pin header title + favicon stay 12px on screen at any camera zoom.
		const boardPinTitleFontSize = isBoard
			? boardPinHeaderScreenPx()
			: `${inverseZoom * 16.8 * boardHeaderScale}px`;
		const boardSpacePx = px => (isBoard ? `${px * boardHeaderScale}px` : undefined);
		const boardZoomPx = boardSpacePx;
		const threadAvatarSx = isBoard
			? {
					width: boardZoomPx(28),
					height: boardZoomPx(28),
					minWidth: boardZoomPx(28),
					minHeight: boardZoomPx(28),
					flexShrink: 0,
				}
			: { width: 28, height: 28, flexShrink: 0 };
		const boardChipSx = isBoard
			? {
					bgcolor: 'transparent',
					borderColor: 'divider',
					height: boardZoomPx(24),
					fontSize: boardZoomPx(12),
					'& .MuiChip-label': { px: boardSpacePx(8) },
				}
			: { bgcolor: 'transparent', borderColor: 'divider' };
		const boardIconSx = isBoard ? { fontSize: boardZoomPx(20) } : undefined;

		const handleBoardPinMouseEnter = () => {
			if (boardPinHoverTimeoutRef.current) {
				clearTimeout(boardPinHoverTimeoutRef.current);
				boardPinHoverTimeoutRef.current = null;
			}
			setIsBoardPinHovered(true);
			props.onBoardPinHover?.();
		};

		const handleBoardPinMouseLeave = () => {
			boardPinHoverTimeoutRef.current = setTimeout(() => {
				setIsBoardPinHovered(false);
				boardPinHoverTimeoutRef.current = null;
			}, 120);
		};

		useEffect(() => () => {
			if (boardPinHoverTimeoutRef.current) clearTimeout(boardPinHoverTimeoutRef.current);
			if (imageHoverEnterTimeoutRef.current) clearTimeout(imageHoverEnterTimeoutRef.current);
		}, []);

		const clearImageHoverEnterTimeout = () => {
			if (imageHoverEnterTimeoutRef.current) {
				clearTimeout(imageHoverEnterTimeoutRef.current);
				imageHoverEnterTimeoutRef.current = null;
			}
		};

		const applyImageHoverZoom = (wrapperEl) => {
			const img = wrapperEl?.querySelector('img');
			if (!img) return;
			const hoverInvZoom = isBoard
				? readBoardInvZoom(wrapperEl)
				: inverseZoom;
			img.style.transition = 'transform 0.5s ease-in';
			img.style.transform = `scale(${getCardImageHoverScale(props.type, hoverInvZoom)})`;
		};

		const handleImageMouseEnter = (e) => {
			const wrapperEl = e.currentTarget;
			clearImageHoverEnterTimeout();
			// Dwell before zoom + HQ upgrade so cursor pass-overs do not fetch full screenshots.
			imageHoverEnterTimeoutRef.current = setTimeout(() => {
				imageHoverEnterTimeoutRef.current = null;
				setIsImageHovered(true);
				applyImageHoverZoom(wrapperEl);
			}, CARD_IMAGE_HOVER_ENTER_DELAY_MS);
		};

		const handleImageMouseLeave = (e) => {
			clearImageHoverEnterTimeout();
			setIsImageHovered(false);
			const img = e.currentTarget.querySelector('img');
			if (!img) return;
			img.style.transformOrigin = lastTransformOriginRef.current;
			img.style.transition = 'transform 1s ease-out';
			img.style.transform = 'scale(1)';
		};

		const showBoardPinComment = isBoardPinHovered;

		const renderPinCommentSection = () => (
			<Box
				className={isBoard ? 'board-pin-comment-area' : undefined}
				sx={{
					...(isBoard
						? {
								padding: boardSpacePx(16),
								width: `max(${BOARD_PIN_COMMENT_MIN_WIDTH_PX}px, 100%)`,
								minWidth: `${BOARD_PIN_COMMENT_MIN_WIDTH_PX}px`,
							}
						: {
								px: 1.5,
								pb: 1,
								pt: 0.5,
							}),
					textAlign: 'left',
					cursor: isBoard ? 'default' : 'zoom-in',
				}}
				onPointerDown={blockCanvasDrag}
				onDragStart={blockCanvasDrag}
				onClick={isBoard ? undefined : handleAreaClick}
			>
				<Box sx={{ display: 'flex', gap: isBoard ? boardSpacePx(16) : 1, alignItems: 'flex-start' }}>
					<Avatar
						src={pinCommentWriter?.profileImageUrl}
						alt={pinCommentWriter?.username || ''}
						sx={threadAvatarSx}
					/>
					<Box sx={{ minWidth: isBoard ? undefined : 0, flex: 1 }}>
						{
							pinCommentWriter?.username
							?
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{
										display: 'block',
										mb: isBoard ? boardSpacePx(2) : 0.25,
										...(isBoard ? { fontSize: boardZoomPx(12) } : {}),
									}}
								>
									@{pinCommentWriter.username}
								</Typography>
							:
								null
						}
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isBoard ? boardSpacePx(4) : 0.5, mb: isBoard ? boardSpacePx(4) : 0.5 }}>
							{(pinComment.hashtags ?? []).map((hashtag, i) => (
								<Chip
									key={hashtag._id ?? `${hashtag.name}-${i}`}
									size="small"
									variant="outlined"
									label={`#${String(hashtag.name ?? '').replace(/^#/, '')}`}
									sx={boardChipSx}
								/>
							))}
							{(pinComment.persontags ?? []).map((pt, i) => (
								<Chip
									key={pt.username ?? pt._id ?? i}
									size="small"
									variant="outlined"
									label={`@${pt.username}`}
									sx={boardChipSx}
								/>
							))}
						</Box>
						<Typography
							variant="body2"
							sx={{
								wordWrap: 'break-word',
								mb: isBoard ? boardSpacePx(8) : 1,
								...(isBoard ? { fontSize: boardZoomPx(14) } : {}),
							}}
						>
							{pinComment.text}
						</Typography>
						<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: isBoard ? boardSpacePx(6) : 0.75 }}>
							<Tooltip title="Like">
								<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
									{(pinComment.userReaction ?? []).length > 0 && (pinComment.userReaction ?? [])[0].type === 1 ? (
										<FavoriteIcon fontSize="small" sx={{ color: 'error.main', ...boardIconSx }} />
									) : (
										<FavoriteBorderIcon fontSize="small" sx={boardIconSx} />
									)}
								</Box>
							</Tooltip>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={isBoard ? { fontSize: boardZoomPx(14) } : undefined}
							>
								{pinComment.likeCount <= 0 ? 0 : pinComment.likeCount}
							</Typography>
							<Tooltip title="Reply">
								<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
									<ReplyIcon fontSize="small" sx={boardIconSx} />
								</Box>
							</Tooltip>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={isBoard ? { fontSize: boardZoomPx(14) } : undefined}
							>
								{pinComment.replyCount ?? 0}
							</Typography>
						</Box>
					</Box>
				</Box>
				{
					lastReply
					?
						<Box sx={{ mt: isBoard ? boardSpacePx(6) : 0.75, pl: isBoard ? boardSpacePx(44) : 4.5 }}>
							{
								moreRepliesCount > 0
								?
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											display: 'block',
											mb: isBoard ? boardSpacePx(4) : 0.5,
											...(isBoard ? { fontSize: boardZoomPx(12) } : {}),
										}}
									>
										{moreRepliesCount} more {moreRepliesCount === 1 ? 'reply' : 'replies'}
									</Typography>
								:
									null
							}
							<Box sx={{ display: 'flex', gap: isBoard ? boardSpacePx(16) : 1, alignItems: 'flex-start' }}>
								<Avatar
									src={lastReplyWriter?.profileImageUrl}
									alt={lastReplyWriter?.username || ''}
									sx={threadAvatarSx}
								/>
								<Box sx={{ minWidth: isBoard ? undefined : 0, flex: 1 }}>
									{
										lastReplyWriter?.username
										?
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{
													display: 'block',
													mb: isBoard ? boardSpacePx(2) : 0.25,
													...(isBoard ? { fontSize: boardZoomPx(12) } : {}),
												}}
											>
												@{lastReplyWriter.username}
											</Typography>
										:
											null
									}
									<Typography
										variant="body2"
										sx={{
											wordWrap: 'break-word',
											...(isBoard ? { fontSize: boardZoomPx(14) } : {}),
										}}
									>
										{lastReply.text}
									</Typography>
								</Box>
							</Box>
						</Box>
					:
						null
				}
			</Box>
		);

		const contextMenu = (
			<Popover
				open={Boolean(contextMenuPosition)}
				anchorReference="anchorPosition"
				anchorPosition={contextMenuPosition || { top: 0, left: 0 }}
				onClose={handleClose}
			>
				<MenuList>
					<MenuItem onClick={handleDeleteActivity}>
						<ListItemText>Delete this pin</ListItemText>
					</MenuItem>
				</MenuList>
			</Popover>
		);

		const coreCardContent = (
			<>
			<CardHeader
				//className='simple_card_action'
				id='NO_BODY_EVENT'
				className={isBoard ? 'board-pin-drag-handle' : undefined}
				onContextMenu={handleContextMenu}
				onPointerDown={
					isBoard && allowPinInteraction && props.onCardPointerDown
						? e => props.onCardPointerDown(e, {
							allowDrag: true,
							onClick: youtubeVideo ? handleAreaClick : undefined,
						})
						: undefined
				}
				sx={{
					...(isBoard
						? {
								cursor: allowPinInteraction ? 'grab' : undefined,
								pointerEvents: pinDragPointerEvents,
								userSelect: 'none',
								boxSizing: 'border-box',
								height: boardPinHeaderHeightPx(),
								minHeight: boardPinHeaderHeightPx(),
								maxHeight: boardPinHeaderHeightPx(),
								padding: `${boardInvZoomPx(6)} ${boardInvZoomPx(10)}`,
								overflow: 'hidden',
								alignItems: 'center',
								'&:active': allowPinInteraction ? { cursor: 'grabbing' } : undefined,
							}
						: {}),
					'& .MuiCardHeader-content': {
						display: 'block',
						overflow: 'hidden',
						minWidth: 0,
					},
				}}
				avatar={
					isBoard
						? undefined
						: (
							<img
								src={'https://' + content.urls[0].host + '/favicon.ico'}
								draggable
								onError={(e) => {
									e.target.onerror = null; // Prevents infinite loop if fallback image also fails
									e.target.src = noFavicon;
								}}
								style={{
									width: `${28 * inverseZoom * boardHeaderScale}px`,
									height: `${28 * inverseZoom * boardHeaderScale}px`,
									minWidth: `${28 * inverseZoom * boardHeaderScale}px`,
									minHeight: `${28 * inverseZoom * boardHeaderScale}px`,
									objectFit: 'cover',
									borderRadius: '50%',
									display: 'block',
								}}
							/>
						)
				}
				title={
					isBoard
						? (
							<>
								<img
									src={'https://' + content.urls[0].host + '/favicon.ico'}
									draggable={false}
									onDragStart={blockCanvasDrag}
									alt=""
									onError={(e) => {
										e.target.onerror = null;
										e.target.src = noFavicon;
									}}
									style={{
										width: '1em',
										height: '1em',
										objectFit: 'cover',
										borderRadius: '50%',
										display: 'inline',
										verticalAlign: '-0.15em',
										marginRight: '0.35em',
									}}
								/>
								{headerTitleText}
							</>
						)
						: boardPinHeaderTitle
				}
				titleTypographyProps={{
					variant:'subtitle1', 
					...(isBoard
						? {
								className: ledgerHeaderText ? 'board-pin-title board-pin-title--ledger' : 'board-pin-title',
								noWrap: true,
								style: {
									fontSize: boardPinTitleFontSize,
									fontWeight: ledgerHeaderText ? 700 : 600,
									textAlign: 'left',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									wordBreak: 'normal',
									lineHeight: 1.2,
									color: ledgerHeaderText ? LEDGER_ACCENT_ON_LIGHT : '#1e1b4b',
								},
							}
						: {
								noWrap: true,
								textOverflow: 'ellipsis',
								style: {
									fontSize: boardPinTitleFontSize,
									textAlign: 'left',
								},
							}),
				}}
			/>
			<div
				className={isBoard ? 'board-pin-image-area' : undefined}
				aria-label={
					youtubeVideo
						? (youtubeVideo.momentLabel
							? `Play YouTube video from ${youtubeVideo.momentLabel}`
							: 'Play YouTube video')
						: undefined
				}
				onClick={isBoard ? undefined : handleAreaClick}
				onPointerDown={
					isBoard && allowPinInteraction && props.onCardPointerDown
						? e => props.onCardPointerDown(e, { onClick: handleAreaClick, allowDrag: false })
						: blockCanvasDrag
				}
				onDragStart={blockCanvasDrag}
				style={{
					textAlign: 'center',
					cursor: allowPinInteraction ? (isBoard ? 'pointer' : 'zoom-in') : undefined,
				}}
				onContextMenu={handleContextMenu}
			>
				{
					youtubeVideo
					?
					<div className="board-pin-youtube">
						<img
							src={pinThumbnail || youtubeVideo.thumbnailUrl}
							alt=""
							draggable={false}
							onDragStart={blockCanvasDrag}
							style={{
								width: '100%',
								aspectRatio: '16 / 9',
								objectFit: 'cover',
								display: 'block',
							}}
						/>
						<YouTubeMomentChip label={youtubeVideo.momentLabel} />
						<span className="board-pin-youtube-play" aria-hidden="true" />
					</div>
					:
					!pinThumbnail
					?
					(pageOnlyPin ? (
						<Box
							sx={{
								px: 1.5,
								py: 1.25,
								textAlign: 'left',
								width: '100%',
								boxSizing: 'border-box',
							}}
						>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									fontSize: isBoard ? boardZoomPx(13) : undefined,
									lineHeight: 1.4,
									display: '-webkit-box',
									WebkitLineClamp: 4,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
								}}
							>
								{getPinSnippet(props.activity) || content.title || 'Saved page'}
							</Typography>
						</Box>
					) : null)
					:
					<>
								<div
									style={{
										position: 'relative',
										overflow: 'hidden',
										width: imageAreaWidth,
										maxWidth: '100%',
									}}
									onMouseEnter={handleImageMouseEnter}
									onMouseMove={e => {
										const img = e.currentTarget.querySelector('img');
										if (!img) return;
										const rect = img.getBoundingClientRect();
										const x = ((e.clientX - rect.left) / rect.width) * 100;
										const y = ((e.clientY - rect.top) / rect.height) * 100;
										const origin = `${x}% ${y}%`;
										img.style.transformOrigin = origin;
										lastTransformOriginRef.current = origin;
									}}
									onMouseLeave={handleImageMouseLeave}
								>
								{!imgLoaded && (
								<div
									style={{
									width: imageAreaWidth,
									backgroundColor: grey[300],
									animation: 'pulse 1.5s infinite',
									borderRadius: '4px',
									}}
								/>
								)}
								<LazyImage
									src={pinThumbnail}
									when={isBoard ? props.loadImageWhen : undefined}
									lazy={!isBoard}
									adaptive={hasAdaptiveScreenshot ? {
										tiers: screenshotTiers,
										layoutWidth: cardLayoutWidth,
										viewZoom,
										isHovered: isImageHovered,
									} : undefined}
									draggable={!isBoard}
									onDragStart={blockCanvasDrag}
									onPointerDown={blockCanvasDrag}
									onError={() => setImgLoaded(false)}
									onLoad={() => setImgLoaded(true)}
									style={{
										maxWidth: '100%',
										width: '100%',
										...(String(pinThumbnail || '').includes('i.ytimg.com/') && !isBoard
											? { aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }
											: {}),
										transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
										transformOrigin: 'center',
									}}
								/>
								{!isBoard && savedYouTube?.momentLabel ? (
									<YouTubeMomentChip label={savedYouTube.momentLabel} />
								) : null}
								</div>
							</>
				}
	      </div>
			</>
		);

		if (isBoard) {
			return (
				<>
					<div
						className={classNames('board-pin-card-hover-root', {
							'board-pin-card-hover-root--hovered': showBoardPinComment,
							'board-pin-card-hover-root--pass-through': props.boardPinPassThrough,
						})}
						onMouseEnter={handleBoardPinMouseEnter}
						onMouseLeave={handleBoardPinMouseLeave}
					>
						<Paper
							className={classNames('board-pin-card', {
								'board-pin-card--pass-through': props.boardPinPassThrough,
								'board-pin-card--has-comment': showBoardPinComment && Boolean(pinComment),
							})}
							elevation={0}
						>
							{coreCardContent}
						</Paper>
						{props.type !== 'OBJECT' && pinComment && showBoardPinComment
							? renderPinCommentSection()
							: null}
					</div>
					{contextMenu}
				</>
			);
		}

		return (
			<>
				{coreCardContent}
				{props.type !== 'OBJECT' && pinComment ? renderPinCommentSection() : null}
				{contextMenu}
			</>
		);
		
}

export default Content;
